import { z } from 'zod';
const maxClients = 16;
const maxOrganizations = 16;
const maxPermissionNames = 32;
const maxIdentifierLength = 21;
const maxPermissionLength = 256;
const parseHttpsResource = (value) => {
    try {
        return new URL(value);
    }
    catch {
        // The surrounding guard reports malformed URLs without leaking parser details.
    }
};
const httpsResourceGuard = z
    .string()
    .min(1)
    .max(2048)
    .superRefine((value, context) => {
    const url = parseHttpsResource(value);
    if (!url) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'resource must be an absolute URL',
        });
        return;
    }
    if (url.protocol !== 'https:' || url.username || url.password || value.includes('#')) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'resource must be an HTTPS URL without credentials or a fragment',
        });
    }
});
const identifierGuard = z.string().min(1).max(maxIdentifierLength);
const permissionNameGuard = z.string().min(1).max(maxPermissionLength);
export const workloadDirectoryClientGuard = z
    .object({
    clientId: identifierGuard,
    applicationId: identifierGuard,
    /** The dedicated directory API resource used as the access-token audience. */
    resource: httpsResourceGuard,
    /** The JXX business API resource whose current organization scopes are published. */
    permissionResource: httpsResourceGuard,
    organizationIds: identifierGuard.array().min(1).max(maxOrganizations),
    permissionNames: permissionNameGuard.array().max(maxPermissionNames),
    ownerPermission: permissionNameGuard.optional(),
    /** Opts this dedicated M2M client into bounded native-organization member listing. */
    allowMemberListing: z.boolean().optional(),
})
    .strict()
    .superRefine(({ resource, permissionResource, organizationIds, permissionNames, ownerPermission }, context) => {
    if (resource === permissionResource) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['permissionResource'],
            message: 'permissionResource must be distinct from the directory resource',
        });
    }
    if (new Set(organizationIds).size !== organizationIds.length) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['organizationIds'],
            message: 'organizationIds must be unique',
        });
    }
    if (new Set(permissionNames).size !== permissionNames.length) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['permissionNames'],
            message: 'permissionNames must be unique',
        });
    }
    if (ownerPermission !== undefined && !permissionNames.includes(ownerPermission)) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['ownerPermission'],
            message: 'ownerPermission must be included in permissionNames',
        });
    }
});
export const workloadDirectoryConfigGuard = z
    .object({
    clients: workloadDirectoryClientGuard.array().max(maxClients),
})
    .strict()
    .superRefine(({ clients }, context) => {
    for (const field of ['clientId', 'applicationId', 'resource']) {
        const values = clients.map((client) => client[field]);
        if (new Set(values).size !== values.length) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['clients'],
                message: `client ${field} values must be unique`,
            });
        }
    }
});
/** Applied when the row is absent: no client is enabled and the endpoint is not found. */
export const defaultWorkloadDirectoryConfig = Object.freeze({
    clients: [],
});
